// TabContent1.js
import React, { useState, useEffect } from 'react';
import ProgressionTab from '../../../../theme/ProgressionBuilder/ProgressionTab';
import { useTheme } from '@emotion/react';
import Text from '../../../../theme/text/Text';
import { useTranslation } from 'react-i18next';
import { Box, ButtonBase, InputBase } from '@mui/material';
import { tokens } from '../../../../../theme';
import NumberInput from '../../../../theme/inputs/NumberInput';
import ShDateRangePicker from "../../../../theme/ShDateRangePicker";
import ShValidatedInput from '../../../../theme/inputs/ShValidatedInput';
import { ENV_TYPES } from "../../../Sidebar";
import { useDispatch, useSelector } from 'react-redux';
import * as yup from "yup";
import { yupNumberRequired, yupRequired } from '../../../../../utils/validation';
import { setTabErrors } from '../../../../../newapi/global/progressionTabSlice';
import { useGetRelationsSelectQuery } from '../../../../../newapi/global/relation/relationSlice';
import { useGetWebshopCredentialsSelectQuery } from '../../../../../newapi/webshop/webshopSlice';
import ShValidatedInputDropdown from '../../../../theme/dropdowns/ShValidatedInputDropdown';
import ShSwitch from "../../../../theme/inputs/ShSwitch";
import { useGetSettingsQuery } from "../../../../../newapi/global/generalApiSlice";
import EMGeneralExplainerBox from "../../../ModalsPopover/explainers/EMGeneralExplainerBox";
import { useGetOrderFieldsQuery, useGetOrderFieldsSelectQuery } from '../../../../../newapi/order/orderFieldSlice';
import { useGetWarehousesSelectQuery, useGetWarehouseQuery } from '../../../../../newapi/warehouse/warehouseApiSlice';
import { countriesFromLocale } from '../../../../../utils/countries';
import { booleanValueTypes, dropdownValueTypes, getListOptions, numberValueTypes, textValueTypes } from '../../../../Streams/Conditions/ConditionSettings';
import TextInput from '../../../../theme/inputs/TextInput';
import Subheading from '../../../../theme/text/Subheading';
import { InputDropdown } from '../../../../theme/dropdowns/InputDropdown';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { buildPickupMethodRemark, parsePickupMethodRemark } from '../../../../../utils/helpers';

/**
 * TabContent1 Component
 * 
 * Props:
 * - handleDataUpdate: Function, updates the data in the Redux state.
 * - handleNext: Function, navigates to the next tab.
 */
const Content = (props) => {
    const theme = useTheme();
    const {t} = useTranslation();
    const colors = tokens(theme.palette.mode);
    // const data = { ...props.data };
    const [loaded, setLoaded] = useState(false);
    const dispatch = useDispatch();
    const { data: relations, isLoading } = useGetRelationsSelectQuery();
    const {data: dropshippingEnabled, isLoading: isLoadingSettings} = useGetSettingsQuery("dropshipping_enabled");

    const [showDropshipping, setShowDropshipping] = useState(false);
    useEffect(() => {
        if (dropshippingEnabled) {
            setShowDropshipping(dropshippingEnabled?.find(setting => setting.key === "dropshipping_enabled")?.value === "true");
        }
    }, dropshippingEnabled)

    // Get values to determine if the relation dropdown should be shown
    const isFulfilment = useSelector((state) => state.settings.environment.isFulfilment);
    const selectedEnvironmentType = useSelector((state) => state.settings.environment.selectedEnvironmentType);
    const isRelation = selectedEnvironmentType === ENV_TYPES[200];
    const selectedId = useSelector((state) => state.settings.environment.selectedId);

    useEffect(() => {
        if(!isFulfilment && !(selectedEnvironmentType === ENV_TYPES[200])) {
            handleInput("relationId",1);
        }
    },[isFulfilment,selectedEnvironmentType])

    const {data: orderFields, isLoading: orderFieldsLoading} = useGetOrderFieldsSelectQuery();

    // Cash register order: only relevant for non-fulfilment, non-relation environments
    const showCashRegister = !isFulfilment && !isRelation;
    const selectedActiveWarehouseId = useSelector((state) => state.warehouse.activeId);
    const { data: warehousesData } = useGetWarehousesSelectQuery(undefined, { skip: !showCashRegister });
    const warehouses = warehousesData?.data ?? [];

    // Resolve effective warehouse id: explicit choice → active warehouse → single warehouse → none
    const effectiveWarehouseId = props.localData.warehouseId
        ?? (selectedActiveWarehouseId !== -1 ? selectedActiveWarehouseId : null)
        ?? (warehouses.length === 1 ? warehouses[0].id : null);

    const shouldFetchWarehouse = !!(props.localData.isCashRegister && effectiveWarehouseId);
    const { data: warehouseDetails } = useGetWarehouseQuery(effectiveWarehouseId, {
        skip: !shouldFetchWarehouse
    });

    const language = useSelector((state) => state.settings.language);
    const countries = countriesFromLocale(language);
    const warehouseCountryName = warehouseDetails?.country
        ? countries?.find(c => c.alpha2 === warehouseDetails.country)?.name ?? warehouseDetails.country
        : null;

    // Populate shippingAddress from warehouse details when kassa is enabled
    useEffect(() => {
        if (!props.localData.isCashRegister || !warehouseDetails) return;

        const houseSuffix = [warehouseDetails.house_nr, warehouseDetails.house_nr_extra]
            .filter(Boolean)
            .join(' ');
        const address1 = [warehouseDetails.street, houseSuffix].filter(Boolean).join(' ').trim();

        props.setLocalData(prev => ({
            ...prev,
            warehouseId: warehouseDetails.id,
            shippingAddress: {
                ...prev.shippingAddress,
                firstName: 'Klant',
                lastName: 'Kassa',
                address1,
                postal: warehouseDetails.postal ?? '',
                city: warehouseDetails.city ?? '',
                country_code: warehouseDetails.country ?? prev.shippingAddress?.country_code,
                company: warehouseDetails.name ?? prev.shippingAddress?.company,
                phone: warehouseDetails.phone ?? prev.shippingAddress?.phone,
            }
        }));
    }, [warehouseDetails, props.localData.isCashRegister]);

    const handleInput = (key, val) => {
        // props.handleDataUpdate({ [key]: val, ...(key === "relationId" && { webshopCredentialsId: null }) });
        // if (key === "isPickup") {
        //     props.handleDataUpdate({ deliveryMethod: val ? "pickup" : "regular" });
        // } else if (key === "deliveryMethod" && val === "dropshipping" && data.isPickup) {
        //     props.handleDataUpdate({ isPickup: false });
        // }

        // Update main key
        props.setLocalData(prev => ({
            ...prev,
            [key]: val,
            ...(key === "relationId" && { webshopCredentialsId: null }),
        }));

        // Special logic for isPickup
        if (key === "isPickup") {
            props.setLocalData(prev => ({
                ...prev,
                deliveryMethod: val ? "pickup" : "regular",
                ...(!val && { remark: parsePickupMethodRemark(prev.remark).remark }),
            }));
        } else if (key === "deliveryMethod" && val === "dropshipping" && props.localData.isPickup) {
            // If switching to dropshipping while pickup was true
            props.setLocalData(prev => ({
                ...prev,
                isPickup: false,
            }));
        }

        dispatch(setTabErrors({ ...props.errors, [key]: '' }));
    };

    // The pickup method is stored at the end of the remark
    const pickupRemark = parsePickupMethodRemark(props.localData.remark);
    const pickupMethodOptions = [
        { id: "boxes", name: t("loose_boxes") },
        { id: "pallet", name: t("pallet") },
    ];
    const handlePickupMethodInput = (key, val) => {
        const next = { ...pickupRemark, [key]: val };
        handleInput("remark", buildPickupMethodRemark(next.remark, next.pickupMethod, next.maxHeight));
    };

    // const select = (date) => {
    //     const formattedDate = date[0].startDate.toISOString().split('T')[0];
    //     props.handleDataUpdate({ date_planned: formattedDate });
    // }

    const select = (date) => {
        const formattedDate = date[0].formattedStartDate;
        props.setLocalData(prev => ({
            ...prev,
            date_planned: formattedDate
        }));
    };

    useEffect(() => {
        if (loaded) {
            props.setLocalData(prev => ({
                ...prev,
                isVirtual: true
            }));
        }
        setLoaded(true);
    }, [loaded]);

    // Set virtual product is true for POST request
    // useEffect(() => {
    //     if(loaded) {
    //         props.handleDataUpdate({ isVirtual: true });
    //     }
    //     setLoaded(true);
    // }, [loaded]);

    // const [orderFieldValues, setOrderFieldValues] = useState([]);
    

    return (
        <>
            <Box 
                sx={{
                    display:'flex',
                    flexDirection:"column",
                }}
            >
                {isFulfilment && !isRelation && (
                    <Box 
                        sx={{
                            width:1,
                            display:"flex",
                            paddingTop: 1.5,
                            pb:1.5,
                            alignItems:"center",
                            justifyContent:"space-between",
                            gap:7,
                        }}
                    >
                        
                        <Box
                            sx={{
                                width:140,
                                display:"flex",
                                alignItems:"center",
                            }}
                        >
                            <Text>{t("relation")}</Text>
                        </Box>
                        <Box
                            sx={{
                                flex:1,
                                backgroundColor : `${theme.palette.mode === "dark" ?colors.grey[300] : colors.grey[100]}`,
                                borderRadius:4,
                                display:"flex",
                            }}
                        >
                            <ShValidatedInputDropdown
                                key={props.localData.relationId}
                                displayName={"name"} 
                                changeField={"id"}
                                valueKey={"id"}
                                closeOnSelection
                                searchOptions={{
                                    enabled: true,
                                    placeholder: t("search_relation_placeholder")
                                }}
                                styles={{
                                    minWidth:300
                                }}

                                disallowDeselection
                                name="relationId"
                                selected={relations?.find(item => item.id === props.localData.relationId)?.id ?? -1}
                                options={relations ? relations : []}
                                noSelection={t("choose_option")} 
                                onChange={handleInput}
                                error={props.errors.relationId}
                            />
                        </Box>
                    </Box>
                )}
                <Box 
                    sx={{
                        width:1,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    <Box
                        sx={{
                            width:140,
                        }}
                    >
                        <Text>{t("reference")}</Text>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            borderRadius:4,
                            display:"flex",
                        }}
                    >
                        <ShValidatedInput
                            name="webshopreference"
                            value={props.localData.webshopreference}
                            onChange={handleInput}
                            error={props.errors.webshopreference}
                        />
                    </Box>
                </Box>
                <Box 
                    sx={{
                        width:1,
                        paddingTop: 1.5,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    
                    <Box
                        sx={{
                            width:140,
                            display:"flex",
                            alignItems:"center",
                        }}
                    >
                        <Text>{t("date_planned")}</Text>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            backgroundColor : `${theme.palette.mode === "dark" ?colors.grey[300] : colors.grey[100]}`,
                            borderRadius:4,
                            display:"flex",
                            height:32
                        }}
                    >
                        <ShDateRangePicker 
                            singleSelect={true} 
                            select={select} 
                            size={32}
                        >

                        </ShDateRangePicker>
                    </Box>
                    
                </Box>
                <Box 
                    sx={{
                        width:1,
                        paddingTop: 1.5,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    
                    <Box
                        sx={{
                            width:140,
                            display:"flex",
                            alignItems:"center",
                        }}
                    >
                        <Text>{t("pickup_order")}</Text>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            borderRadius:4,
                            display:"flex",
                            height:32
                        }}
                    >
                    <ShSwitch
                        checked={props.localData.isPickup ?? false}
                        onChange={(e) => handleInput('isPickup', e.target.checked)}
                    />
                    </Box>
                </Box>
                {props.localData.isPickup && (
                    <Box
                        sx={{
                            width:1,
                            paddingTop: 1.5,
                            display:"flex",
                            alignItems:"center",
                            justifyContent:"space-between",
                            gap:7
                        }}
                    >
                        <Box
                            sx={{
                                width:140,
                                display:"flex",
                                alignItems:"center",
                            }}
                        >
                            <Text>{t("pickup_method")}</Text>
                        </Box>
                        <Box
                            sx={{
                                flex:1,
                                backgroundColor : `${theme.palette.mode === "dark" ?colors.grey[300] : colors.grey[100]}`,
                                borderRadius:4,
                                display:"flex",
                            }}
                        >
                            <ShValidatedInputDropdown
                                displayName={"name"}
                                valueKey={"id"}
                                closeOnSelection
                                disallowDeselection
                                name="pickupMethod"
                                selected={pickupRemark.pickupMethod ?? -1}
                                options={pickupMethodOptions}
                                noSelection={t("choose_option")}
                                onChange={handlePickupMethodInput}
                            />
                        </Box>
                    </Box>
                )}
                {props.localData.isPickup && pickupRemark.pickupMethod === "pallet" && (
                    <Box
                        sx={{
                            width:1,
                            paddingTop: 1.5,
                            display:"flex",
                            alignItems:"center",
                            justifyContent:"space-between",
                            gap:7
                        }}
                    >
                        <Box
                            sx={{
                                width:140,
                                display:"flex",
                                alignItems:"center",
                            }}
                        >
                            <Text>{t("max_height")} (cm)</Text>
                        </Box>
                        <Box
                            sx={{
                                flex:1,
                                display:"flex",
                            }}
                        >
                            <NumberInput
                                btnSize={"24"}
                                height={32}
                                maxWidth={160}
                                onlyPositive
                                min={0}
                                value={pickupRemark.maxHeight ?? ''}
                                change={(val) => handlePickupMethodInput("maxHeight", val)}
                            />
                        </Box>
                    </Box>
                )}
                <Box
                    sx={{
                        width:1,
                        paddingTop: 1.5,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >

                    <Box
                        sx={{
                            width:140,
                            display:"flex",
                            alignItems:"center",
                        }}
                    >
                        <Text>{t("b2b_order")}</Text>
                        <EMGeneralExplainerBox title={t('em_b2b_title')} text={t('em_b2b_text')}/>
                    </Box>

                    <Box
                        sx={{
                            flex:1,
                            borderRadius:4,
                            display:"flex",
                            height:32
                        }}
                    >
                    <ShSwitch
                        checked={props.localData.isB2B ?? false}
                        onChange={(e) => handleInput('isB2B', e.target.checked)}
                    />
                    </Box>
                </Box>
                {showCashRegister && (
                    <Box
                        sx={{
                            width:1,
                            paddingTop: 1.5,
                            display:"flex",
                            alignItems:"center",
                            justifyContent:"space-between",
                            gap:7
                        }}
                    >
                        <Box
                            sx={{
                                width:140,
                                display:"flex",
                                alignItems:"center",
                            }}
                        >
                            <Text>{t("cash_register_order")}</Text>
                            <EMGeneralExplainerBox title={t('em_cash_register_title')} text={t('em_cash_register_text')}/>
                        </Box>
                        <Box
                            sx={{
                                flex:1,
                                borderRadius:4,
                                display:"flex",
                                height:32
                            }}
                        >
                            <ShSwitch
                                checked={props.localData.isCashRegister ?? false}
                                onChange={(e) => handleInput('isCashRegister', e.target.checked)}
                            />
                        </Box>
                    </Box>
                )}
                {showCashRegister && props.localData.isCashRegister && warehouses.length > 1 && (
                    <Box
                        sx={{
                            width:1,
                            display:"flex",
                            paddingTop: 1.5,
                            alignItems:"center",
                            justifyContent:"space-between",
                            gap:7,
                        }}
                    >
                        <Box
                            sx={{
                                width:140,
                                display:"flex",
                                alignItems:"center",
                            }}
                        >
                            <Text>{t("warehouse")}</Text>
                        </Box>
                        <Box
                            sx={{
                                flex:1,
                                backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`,
                                borderRadius:4,
                                display:"flex",
                            }}
                        >
                            <ShValidatedInputDropdown
                                key={effectiveWarehouseId}
                                displayName={"name"}
                                changeField={"id"}
                                valueKey={"id"}
                                closeOnSelection
                                disallowDeselection
                                searchOptions={{
                                    enabled: true,
                                    placeholder: t("search")
                                }}
                                styles={{
                                    minWidth:300
                                }}
                                name="warehouseId"
                                selected={warehouses.find(item => item.id === effectiveWarehouseId)?.id ?? -1}
                                options={warehouses}
                                noSelection={t("select_warehouse")}
                                onChange={handleInput}
                            />
                        </Box>
                    </Box>
                )}
                {showCashRegister && props.localData.isCashRegister && warehouseDetails && (
                    <Box
                        sx={{
                            width:1,
                            paddingTop: 1.5,
                            display:"flex",
                            alignItems:"flex-start",
                            justifyContent:"space-between",
                            gap:7
                        }}
                    >
                        <Box
                            sx={{
                                width:140,
                                display:"flex",
                                alignItems:"center",
                            }}
                        >
                            <Text>{t("address")}</Text>
                        </Box>
                        <Box
                            sx={{
                                flex:1,
                                background: colors.grey[50],
                                borderRadius:4,
                                px:1.5,
                                py:1,
                                display:"flex",
                                flexDirection:"column",
                                gap:0.25,
                            }}
                        >
                            <Text>{warehouseDetails.name}</Text>
                            <Text>
                                {[warehouseDetails.street, warehouseDetails.house_nr, warehouseDetails.house_nr_extra].filter(Boolean).join(' ')}
                            </Text>
                            <Text>
                                {[warehouseDetails.postal, warehouseDetails.city].filter(Boolean).join(' ')}
                            </Text>
                            {warehouseCountryName && <Text>{warehouseCountryName}</Text>}
                        </Box>
                    </Box>
                )}
                <Box 
                    sx={{
                        width:1,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        paddingTop: 1.5,
                        gap:7
                    }}
                >
                    <Box
                        sx={{
                            width:140,
                        }}
                    >
                        <Text>{t("remark")}</Text>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            borderRadius:4,
                            display:"flex",
                        }}
                    >
                        <ShValidatedInput
                            name="remark"
                            value={pickupRemark.remark}
                            onChange={handlePickupMethodInput}
                            error={props.errors.remark}
                        />
                    </Box>
                </Box>
                {orderFields && orderFields.length > 0 && (

                    <Box
                        sx={{
                            mx:"-12px",
                            px:1.5,
                            pt:1,
                            pb:1.5,
                            mt:3,
                            background:colors.grey[50],
                            display:"flex",
                            gap:1,
                            flexDirection:"column"
                        }}
                    >
                        <Subheading>{t("custom_fields")}</Subheading>
                        {props.orderFieldValues.map((field,idx) => (
                            <Box
                                key={"field"+idx}
                                sx={{
                                    display:"flex",
                                    alignItems:'center',
                                    justifyContent:'space-between',
                                }}
                            >
                                <Box
                                    sx={{
                                        width:140,
                                        mr:7,
                                    }}
                                >
                                    <Text>{field.name}</Text>
                                </Box>
                                <Box
                                    sx={{
                                        flex:1,
                                    }}
                                >
                                    {(textValueTypes.includes(field.valueType) || numberValueTypes.includes(field.valueType)) &&
                                        
                                        <Box
                                            sx={{
                                                background: colors.grey[100],
                                                height: 32,
                                                borderRadius: 4,
                                                width: 1,
                                                "&:hover": {
                                                    background: colors.grey[200],
                                                    cursor: "pointer",
                                                },
                                            }}
                                        >
                                            <TextInput
                                                type={textValueTypes.includes(field.valueType) ? "text" : "number"}
                                                value={field.value}
                                                background="transparent"
                                                onChange={(e) => {
                                                    const newValue = e.target.value;

                                                    props.setOrderFieldValues(prev =>
                                                        prev.map(f =>
                                                        f.orderFieldId === field.orderFieldId
                                                            ? { ...f, value: newValue } // update the value
                                                            : f
                                                        )
                                                    );
                                                }}
                                                
                                            />
                                            
                                        </Box>
                                    }
                                    {booleanValueTypes.includes(field.valueType) &&
                                        <Box>
                                            <ShSwitch
                                                checked={!!field.value}
                                                onChange={(e) => {
                                                    const checked = e.target.checked;

                                                    props.setOrderFieldValues((prev) =>
                                                        prev.map((f) =>
                                                            f.orderFieldId === field.orderFieldId
                                                            ? { ...f, value: checked }
                                                            : f
                                                        )
                                                    );
                                                }}
                                            />
                                        </Box>
                                    }
                                    {dropdownValueTypes.includes(field.valueType) &&
                                        <Box
                                            sx={{
                                                background: colors.grey[100],
                                                borderRadius: 4,
                                                width: 1,
                                            }}
                                        >
                                            <InputDropdown
                                                searchOptions={{
                                                    enabled: true,
                                                    placeholder: t("search")
                                                }}
                                                closeOnSelection
                                                selected={field.listValues?.findIndex(item => item === field.value) ?? -1}
                                                options={field.listValues ?? []}
                                                onChange={(selectedIndex) => {
                                                    const selectedValue = field.listValues[selectedIndex];

                                                    props.setOrderFieldValues(prev =>
                                                        prev.map(f =>
                                                            f.orderFieldId === field.orderFieldId
                                                                ? { ...f, value: selectedValue }
                                                                : f
                                                        )
                                                    );
                                                }}
                                            />
                                        </Box>
                                    }

                                </Box>
                                <ButtonBase
                                    onClick={() => {
                                        props.setOrderFieldValues(prev => prev.filter(f => f.orderFieldId !== field.orderFieldId));
                                    }}
                                    sx={{
                                        width:32,
                                        height:32,
                                        color:colors.red[400],
                                        ml:1,
                                        borderRadius:4,
                                    }}
                                >
                                    <DeleteOutlineOutlinedIcon/>
                                </ButtonBase>
                            </Box>
                        ))}
                        <InputDropdown
                            searchOptions={{
                                enabled:true,
                                placeholder:t("search")
                            }}
                            resetOnSelection
                            closeOnSelection
                            selected={-1}
                            options={orderFields}
                            onChange={(selectedIndex) => {
                                const selectedField = orderFields[selectedIndex];

                                props.setOrderFieldValues((prev) => {
                                    const exists = prev.some(f => f.orderFieldId === selectedField.id);
                                    if (exists) return prev;

                                    return [
                                        ...prev,
                                        {
                                            orderFieldId: selectedField.id,
                                            name: selectedField.name,
                                            valueType: selectedField.valueType,
                                            listValues: selectedField.listValues ?? [],
                                            ...(selectedField.valueType === 'boolean' ? { value: false } : {}),
                                        }
                                    ];
                                });
                            }}
                        />
                    </Box>
                )}
                
            </Box>
        </>
    );
};

const Details = (props) => {
    const {t} = useTranslation();
    const schema = yup.object({
        webshopreference: yup.string().min(5).max(35).required(t('webshopreference'))
    });
    
    const data = useSelector((state) => state.progressiontabs.data);
    const [localData, setLocalData] = useState(data);
    const [orderFieldValues, setOrderFieldValues] = useState(data.orderFieldValues ?? []);

    useEffect(() => {
        setLocalData(data);
    },[data])
    
    useEffect(() => {
        setLocalData(prev => ({
            ...prev,
            orderFieldValues: orderFieldValues
        }));
    }, [orderFieldValues]);

    return (
        <ProgressionTab
            tabId="tabDetails"
            subtitle={t("Details")}
            step={1}
            nextTab={localData.isCashRegister ? "tabOrderlines" : "tabAddressDetails"}
            maxSteps={localData.isCashRegister ? 2 : 3}
            schema={schema}
            updateDataWithValidation={localData} //updates pre-tab-switch
        >
            <Content
                localData={localData} 
                setLocalData={setLocalData}
                orderFieldValues={orderFieldValues}
                setOrderFieldValues={setOrderFieldValues}
            />
        </ProgressionTab>
    )
}

export default Details;

